import api from '../../api';

export type RequiredUpload = {
  key: string;
  label: string;
  types: string[];
  required: boolean;
};

export type TaskRunSummary = {
  id: number;
  name: string;
  status: 'ready' | 'running' | 'completed' | 'failed';
  userId: number;
  userName: string | null;
  staleSkillVersion: boolean;
  updatedAt: string;
};

export type TaskNode = {
  id: number;
  name: string;
  summary: string;
  requiredUploads: RequiredUpload[];
  modelId: string | null;
  version: number;
  isDraft?: boolean;
  userHidden: boolean;
  adminHidden: boolean;
  deleted?: boolean;
  purgeAfter?: string | null;
  runs: TaskRunSummary[];
};

export type ProjectNode = {
  id: number;
  name: string;
  code: string | null;
  userHidden: boolean;
  adminHidden: boolean;
  deleted?: boolean;
  purgeAfter?: string | null;
  memberCount: number;
  tasks: TaskNode[];
};

export type ClientNode = {
  id: number;
  name: string;
  tagType: string;
  userHidden: boolean;
  adminHidden: boolean;
  projects: ProjectNode[];
};

export type TaskFile = {
  id: number;
  kind: 'upload' | 'artifact';
  originalName: string;
  mimeType?: string | null;
  uploadKey?: string | null;
  fileSize?: number | null;
  createdAt?: string;
};

export type SkillVersion = {
  id: number;
  version: number;
  createdAt: string;
  summary?: string | null;
  isCurrent: boolean;
  skillMd?: string | null;
};

export type TaskMessage = {
  id: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
};

export type TaskRun = {
  id: number;
  skillId: number;
  userId: number;
  name: string;
  status: TaskRunSummary['status'];
  additionalComments: string | null;
  staleSkillVersion: boolean;
  skillVersionId: number | null;
  createdAt: string;
  updatedAt: string;
  files: TaskFile[];
  messages?: TaskMessage[];
  user?: { id: number; name: string; email: string };
};

export type SkillPackageFile = {
  id: number;
  path: string;
  kind: 'reference' | 'asset' | 'script' | 'other';
  originalName: string;
  mimeType?: string | null;
  fileSize?: number | null;
};

export type SkillImportWarning = {
  code: string;
  message: string;
};

export type SkillAuthoring = {
  id: number;
  projectId: number;
  name: string;
  isDraft: boolean;
  draftSkillMd: string | null;
  modelId: string | null;
  publishedAt: string | null;
  currentVersionId: number | null;
  authoringBusy?: boolean;
  messages: TaskMessage[];
  files: { id: number; originalName: string; mimeType?: string | null; fileSize?: number | null }[];
  packageFiles?: SkillPackageFile[];
};

export const tasksApi = {
  capability: () => api.get('/api/tasks/capability'),
  tree: (showHidden = false, showDeleted = false) =>
    api.get('/api/tasks/tree', {
      params: {
        showHidden: showHidden ? '1' : undefined,
        showDeleted: showDeleted ? '1' : undefined,
      },
    }),
  createProject: (body: { clientTagId: number; name: string; code?: string }) =>
    api.post('/api/tasks/projects', body),
  patchProject: (projectId: number, body: { name: string; code?: string }) =>
    api.patch(`/api/tasks/projects/${projectId}`, body),
  createSkillDraft: (projectId: number, modelId?: string) =>
    api.post(`/api/tasks/projects/${projectId}/skill-drafts`, { modelId }),
  getAuthoring: (skillId: number) => api.get(`/api/tasks/skills/${skillId}/authoring`),
  patchSkill: (skillId: number, body: { modelId?: string | null; name?: string }) =>
    api.patch(`/api/tasks/skills/${skillId}`, body),
  uploadSkillContext: (skillId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/api/tasks/skills/${skillId}/authoring/uploads`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importSkill: (skillId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/api/tasks/skills/${skillId}/import`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  authorMessage: (skillId: number, content: string, modelId?: string) =>
    api.post(`/api/tasks/skills/${skillId}/authoring/messages`, { content, modelId }),
  publishSkill: (skillId: number) => api.post(`/api/tasks/skills/${skillId}/publish`),
  skillVersions: (skillId: number) => api.get(`/api/tasks/skills/${skillId}/versions`),
  getSkillVersion: (skillId: number, versionId: number) =>
    api.get(`/api/tasks/skills/${skillId}/versions/${versionId}`),
  restoreSkillVersion: (skillId: number, versionId: number) =>
    api.post(`/api/tasks/skills/${skillId}/versions/${versionId}/restore`),
  members: (projectId: number) => api.get(`/api/tasks/projects/${projectId}/members`),
  availableCollaborators: (projectId: number) =>
    api.get(`/api/tasks/projects/${projectId}/available-collaborators`),
  addMember: (projectId: number, userId: number) =>
    api.post(`/api/tasks/projects/${projectId}/members`, { userId }),
  removeMember: (projectId: number, userId: number) =>
    api.delete(`/api/tasks/projects/${projectId}/members/${userId}`),
  hide: (entityType: string, entityId: number) =>
    api.post('/api/tasks/hide', { entityType, entityId }),
  unhide: (entityType: string, entityId: number) =>
    api.delete('/api/tasks/hide', { data: { entityType, entityId } }),
  adminHide: (entityType: string, entityId: number) =>
    api.post('/api/tasks/admin-hide', { entityType, entityId }),
  adminUnhide: (entityType: string, entityId: number) =>
    api.delete('/api/tasks/admin-hide', { data: { entityType, entityId } }),
  deleteProject: (projectId: number) => api.post(`/api/tasks/projects/${projectId}/delete`),
  deleteTask: (skillId: number) => api.post(`/api/tasks/skills/${skillId}/delete`),
  restoreProject: (projectId: number) => api.post(`/api/tasks/projects/${projectId}/restore`),
  restoreTask: (skillId: number) => api.post(`/api/tasks/skills/${skillId}/restore`),
  getTask: (skillId: number) => api.get(`/api/tasks/skills/${skillId}`),
  createRun: (skillId: number, name?: string) =>
    api.post(`/api/tasks/skills/${skillId}/runs`, { name }),
  getRun: (runId: number) => api.get(`/api/tasks/runs/${runId}`),
  patchRun: (runId: number, body: { name?: string; additionalComments?: string }) =>
    api.patch(`/api/tasks/runs/${runId}`, body),
  upload: (runId: number, file: File, uploadKey?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (uploadKey) form.append('uploadKey', uploadKey);
    return api.post(`/api/tasks/runs/${runId}/uploads`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  execute: (runId: number, additionalComments?: string) =>
    api.post(`/api/tasks/runs/${runId}/execute`, { additionalComments }),
  sendMessage: (runId: number, content: string) =>
    api.post(`/api/tasks/runs/${runId}/messages`, { content }),
  downloadUrl: (runId: number, fileId: number) =>
    `/api/tasks/runs/${runId}/files/${fileId}/download`,
};
